import { useEffect, useLayoutEffect, useRef, useSyncExternalStore, type ReactNode } from "react";
import { Box } from "@mui/joy";
import { CssVarsProvider, useColorScheme } from "@mui/joy/styles";
import type { SxProps } from "@mui/joy/styles/types";
import { useDesktopChrome, usePlatform, useAppearancePolicy } from "../platform/PlatformProvider";
import type { AppearanceMode } from "../platform/Platform";
import {
    applyAppearanceAttributes,
    MODE_STORAGE_KEY,
    WEB_V2_MODE_STORAGE_KEY,
    DEFAULT_APPEARANCE_SETTINGS,
    textScaleToSize,
    textSizeToScale,
} from "./appearance";
import {
    getAppearance,
    setAppearance,
    subscribeAppearance,
    type Appearance,
} from "./appearanceStore";
import { classicTheme } from "./classicTheme";
import { primeTheme, type ColorScheme } from "./primeTheme";
// Both web and desktop mount AppearanceProvider. Keep actual Inter assets here
// so each host receives the same 400/500/600 body and chrome weights.
import "@fontsource/overpass/700.css";
import "@fontsource/overpass/800.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/inter/400-italic.css";
import "@fontsource/inter/700-italic.css";
import "@fontsource/overpass/600.css";
import "./tokens.css";
import "./scrollbars.css";

export interface UseAppearanceResult extends Appearance {
    set(patch: Partial<Appearance>): void;
}

/** Reads the appearance store: `{ textSize, density, set(partial) }`. Color
 *  mode lives in Joy's own `useColorScheme()` now, not here. The store is a module
 *  singleton, not React context, so this works with or without `<AppearanceProvider>`
 *  mounted above the caller. */
export function useAppearance(): UseAppearanceResult {
    const appearance = useSyncExternalStore(subscribeAppearance, getAppearance, getAppearance);
    return { ...appearance, set: setAppearance };
}

/** Keeps `platform.appearance` (native menus, the OS chrome) and Joy's own mode in
 *  sync: a change on either side reaches the other. Text scale still bridges through
 *  the `appearanceStore` textSize field. */
function usePlatformAppearanceBridge(appearance: Appearance): void {
    const platform = usePlatform();
    const { mode, setMode } = useColorScheme();
    // The mode a native change just asked Joy to become. `appearanceStore`'s
    // `useSyncExternalStore` notification can force a render before Joy's own `setMode`
    // update has committed, so the push effect below can briefly see a stale `mode` —
    // this guards it from echoing that stale snapshot back to the platform as if it
    // were the real, settled value.
    const pendingMode = useRef<AppearanceMode | null>(null);

    useEffect(() => {
        if (!mode) return;
        if (pendingMode.current !== null && pendingMode.current !== mode) return;
        pendingMode.current = null;
        platform.appearance.set({ mode, textScale: textSizeToScale(appearance.textSize) });
    }, [platform, mode, appearance.textSize]);

    useEffect(() => {
        return platform.appearance.subscribe((next) => {
            pendingMode.current = next.mode;
            setAppearance({ textSize: textScaleToSize(next.textScale) });
            setMode(next.mode);
        });
    }, [platform, setMode]);
}

/** Points what paints outside Joy's components at the resolved scheme: native scrollbars,
 *  form controls and the WebKit overlay bar read the root's `color-scheme` (an inline
 *  declaration beats every stylesheet rule), and the browser tints its toolbar from
 *  `theme-color`, which the boot script in `index.html` set once from the stored mode. The
 *  light body is white in both designs, so the classic window's light reads it here too. */
function applyDocumentScheme(scheme: ColorScheme): void {
    document.documentElement.style.setProperty("color-scheme", scheme);
    document
        .querySelector('meta[name="theme-color"]')
        ?.setAttribute("content", primeTheme.colorSchemes[scheme].palette.background.body);
}

/** Applies Joy's own resolved scheme to the document. Joy resolves `system` to
 *  `light`/`dark` itself (and tracks live OS changes), so there is nothing else to
 *  compute here. */
function useNativeColorScheme(): void {
    const { colorScheme } = useColorScheme();
    useEffect(() => {
        if (colorScheme) applyDocumentScheme(colorScheme);
    }, [colorScheme]);
}

/** Bridges Joy's mode/colorScheme to the platform and the root element. Rendered
 *  inside `<CssVarsProvider>` so it can reach `useColorScheme()`, which `AppearanceProvider`
 *  itself cannot: a component can't consume the context its own return value provides. */
function NativeAppearanceSync({ appearance }: { appearance: Appearance }) {
    usePlatformAppearanceBridge(appearance);
    useNativeColorScheme();
    return null;
}

/** The web's mode is derived from Design v2. It never writes the desktop's
 * saved mode or echoes the web's state through platform.appearance. */
function BrowserAppearanceSync({ redesigned }: { redesigned: boolean }) {
    const { mode, setMode, colorScheme } = useColorScheme();
    const target = redesigned ? colorScheme : "light";
    useLayoutEffect(() => {
        if (!redesigned && mode !== "light") setMode("light");
        if (target) applyDocumentScheme(target);
    }, [mode, setMode, target, redesigned]);
    return null;
}

/** Provides the one Joy theme and the three `data-*` appearance attributes for the
 *  window. Color mode lives in Joy's `CssVarsProvider` (`MODE_STORAGE_KEY`), which owns
 *  its own persistence, system-preference tracking and cross-tab sync. */
export function AppearanceProvider({ children }: { children: ReactNode }) {
    const platform = usePlatform();
    const desktopChrome = useDesktopChrome();
    const { native, redesigned } = useAppearancePolicy();
    const appearance = useAppearance();

    useEffect(() => {
        if (!desktopChrome) return;
        return platform.shortcuts.register({
            key: ",",
            modifiers: ["primary"],
            handler: () => {
                void platform.open({ kind: "url", url: "/settings" });
            },
        });
    }, [platform, desktopChrome]);

    useLayoutEffect(() => {
        document.documentElement.dataset.design = redesigned ? "v2" : "classic";
        applyAppearanceAttributes(
            document.documentElement,
            redesigned ? appearance : DEFAULT_APPEARANCE_SETTINGS,
        );
    }, [appearance, redesigned]);

    return (
        <CssVarsProvider
            key={native ? "native" : redesigned ? "v2" : "classic"}
            theme={redesigned ? primeTheme : classicTheme}
            defaultMode={redesigned ? "system" : "light"}
            modeStorageKey={
                native
                    ? MODE_STORAGE_KEY
                    : redesigned
                      ? WEB_V2_MODE_STORAGE_KEY
                      : `${MODE_STORAGE_KEY}:browser-classic`
            }
        >
            {native ? (
                <NativeAppearanceSync appearance={appearance} />
            ) : (
                <BrowserAppearanceSync redesigned={redesigned} />
            )}
            {children}
        </CssVarsProvider>
    );
}

/** Design v2's own type, radius and font variables (`--joy-fontSize-sm`, `--joy-radius-md`, …)
 *  and its body type, declared on a Design v2 subtree in the classic window. Colors stay out:
 *  the subtree keeps the window's palette. */
const DESIGN_V2_SCOPE_SX = [
    primeTheme.generateCssVars().css,
    { fontFamily: "body", fontSize: "md", lineHeight: "md" },
];

/**
 * A Design v2 screen inside the classic window (`shell/ClassicScreenFrame`). Joy's components in
 * it take `primeTheme`: a nested provider supplies the theme only and generates no stylesheet, so
 * the window keeps the classic theme `AppearanceProvider` gave it. `data-design="v2"` gives the
 * subtree the v2 type and row tokens (`tokens.css`). Popups portal to `<body>`, outside it: they
 * get the theme with the window's token values.
 */
export function DesignV2Scope({ sx, children }: { sx?: SxProps; children: ReactNode }) {
    return (
        <CssVarsProvider theme={primeTheme}>
            <Box data-design="v2" sx={[...DESIGN_V2_SCOPE_SX, ...(Array.isArray(sx) ? sx : [sx])]}>
                {children}
            </Box>
        </CssVarsProvider>
    );
}
